import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ApplicationProvider } from './contexts/ApplicationContext';
import { ShiftProvider } from './contexts/ShiftContext';
import './App.css';
import { ThemeProvider, CssBaseline } from '@mui/material';
import theme from './styles/theme';
import MainLayout from './components/layout/MainLayout';
import LoadingSpinner from './components/common/LoadingSpinner';
import LazyLoadErrorBoundary from './components/common/LazyLoadErrorBoundary';

import { prefetchCriticalRoutes } from './utils/routePrefetch';
// Lazy loaded pages
const Login = React.lazy(() => import('./pages/Login'));
const Register = React.lazy(() => import('./pages/Register'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const JobPostings = React.lazy(() => import('./pages/JobPostings'));
const JobPostingForm = React.lazy(() => import('./pages/JobPostingForm'));
const Applications = React.lazy(() => import('./pages/Applications'));
const ApplicationDetail = React.lazy(() => import('./pages/ApplicationDetail'));
const Interviews = React.lazy(() => import('./pages/Interviews'));
const InterviewForm = React.lazy(() => import('./pages/InterviewForm'));
const Profile = React.lazy(() => import('./pages/Profile'));
const NotFound = React.lazy(() => import('./pages/NotFound'));
const ShiftManagement = React.lazy(() => import('./pages/ShiftManagement'));
const DepartmentManagement = React.lazy(() => import('./pages/DepartmentManagement'));
const DepartmentAdminRegister = React.lazy(() => import('./pages/DepartmentAdminRegister'));
const Notifications = React.lazy(() => import('./pages/Notifications'));
const Chat = React.lazy(() => import('./pages/Chat'));
const ChatRoom = React.lazy(() => import('./pages/ChatRoom'));

// Lazy loaded components for testing
const SocketExample = React.lazy(() => import('./components/SocketExample').then(module => ({ default: module.SocketExample })));

// Loading fallback components using our reusable LoadingSpinner
const LoadingFallback: React.FC<{ message?: string }> = ({ message = 'Loading...' }) => (
  <LoadingSpinner message={message} size="medium" />
);

const FullScreenLoadingFallback: React.FC = () => (
  <LoadingSpinner 
    message="Loading application..." 
    size="large" 
    fullScreen 
  />
);

// Protected route component with layout
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingFallback message="Checking authentication..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return (
    <MainLayout>
      <LazyLoadErrorBoundary>
        <Suspense fallback={<LoadingFallback />}>
          {children}
        </Suspense>
      </LazyLoadErrorBoundary>
    </MainLayout>
  );
};

// Public route component (only accessible when not logged in)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <FullScreenLoadingFallback />;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" />;
  }

  return (
    <LazyLoadErrorBoundary>
      <Suspense fallback={<FullScreenLoadingFallback />}>
        {children}
      </Suspense>
    </LazyLoadErrorBoundary>
  );
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute>
          <Register />
        </PublicRoute>
      } />

      {/* Protected routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/job-postings" element={
        <ProtectedRoute>
          <JobPostings />
        </ProtectedRoute>
      } />
      <Route path="/job-postings/new" element={
        <ProtectedRoute>
          <JobPostingForm />
        </ProtectedRoute>
      } />
      <Route path="/job-postings/:id/edit" element={
        <ProtectedRoute>
          <JobPostingForm />
        </ProtectedRoute>
      } />
      <Route path="/applications" element={
        <ProtectedRoute>
          <ApplicationProvider>
            <Applications />
          </ApplicationProvider>
        </ProtectedRoute>
      } />
      <Route path="/applications/:id" element={
        <ProtectedRoute>
          <ApplicationProvider>
            <ApplicationDetail />
          </ApplicationProvider>
        </ProtectedRoute>
      } />
      <Route path="/interviews" element={
        <ProtectedRoute>
          <Interviews />
        </ProtectedRoute>
      } />
      <Route path="/interviews/new" element={
        <ProtectedRoute>
          <InterviewForm />
        </ProtectedRoute>
      } />
      <Route path="/interviews/:id/edit" element={
        <ProtectedRoute>
          <InterviewForm />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <Profile />
        </ProtectedRoute>
      } />
      <Route path="/shifts" element={
        <ProtectedRoute>
          <ShiftProvider>
            <ShiftManagement />
          </ShiftProvider>
        </ProtectedRoute>
      } />
      <Route path="/department-management" element={
        <ProtectedRoute>
          <DepartmentManagement />
        </ProtectedRoute>
      } />
      <Route path="/department-admin-register" element={
        <ProtectedRoute>
          <DepartmentAdminRegister />
        </ProtectedRoute>
      } />
      <Route path="/notifications" element={
        <ProtectedRoute>
          <Notifications />
        </ProtectedRoute>
      } />
      <Route path="/chat" element={
        <ProtectedRoute>
          <Chat />
        </ProtectedRoute>
      } />
      <Route path="/chat/:roomId" element={
        <ProtectedRoute>
          <ChatRoom />
        </ProtectedRoute>
      } />

      {/* Socket test route */}
      <Route path="/socket-test" element={
        <ProtectedRoute>
          <SocketExample />
        </ProtectedRoute>
      } />

      {/* Redirect from root to dashboard or login */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      
      {/* 404 page */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  // Prefetch critical routes on app initialization
  useEffect(() => {
    // Delay prefetching to avoid blocking initial render
    const timer = setTimeout(() => {
      prefetchCriticalRoutes();
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AuthProvider>
          <NotificationProvider>
          <AppRoutes />
          </NotificationProvider>
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;
